import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { postizAPI } from "../api/postizAPI";

/**
 * Social platform definitions with icons and display names
 * These are the 28+ platforms supported by Postiz
 */
const SOCIAL_PLATFORMS = [
  { id: "instagram", name: "Instagram", icon: "📷", color: "from-pink-400 to-orange-400" },
  { id: "linkedin", name: "LinkedIn", icon: "💼", color: "from-blue-600 to-blue-800" },
  { id: "x", name: "X (Twitter)", icon: "𝕏", color: "from-black to-gray-700" },
  { id: "facebook", name: "Facebook", icon: "f", color: "from-blue-600 to-blue-700" },
  { id: "pinterest", name: "Pinterest", icon: "📌", color: "from-red-500 to-red-700" },
];

const VISIBLE_PLATFORM_IDS = new Set(["instagram", "facebook", "x", "linkedin", "pinterest"]);
const VISIBLE_PLATFORMS = SOCIAL_PLATFORMS.filter((platform) => VISIBLE_PLATFORM_IDS.has(platform.id));
const normalizePlatform = (value = "") => String(value).toLowerCase();

/**
 * PMConnectors - Social platform connection management
 * Displays all available platforms with connect/disconnect functionality
 */
export default function PMConnectors({ token, isDarkMode = false, onConnectorsLoaded }) {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [reconnectingPlatform, setReconnectingPlatform] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(null);

  // Fetch connected platforms on mount
  useEffect(() => {
    if (token) {
      fetchConnectors();
    }
  }, [token]);

  /**
   * Fetch list of connected social platforms from Postiz
   */
  const fetchConnectors = async (options = {}) => {
    const { showLoading = true, showErrorToast = true } = options;
    try {
      if (showLoading) {
        setLoading(true);
        setError(null);
      }
      const result = await postizAPI.getConnectors(token);
      const connectorList = result.data || [];
      setConnectors(connectorList);
      if (onConnectorsLoaded) {
        onConnectorsLoaded(connectorList);
      }
      return connectorList;
    } catch (err) {
      console.error("Error fetching connectors:", err);
      if (showLoading) {
        setError("Failed to load connected platforms. Please try again.");
      }
      if (showErrorToast) {
        toast.error("Failed to load connected platforms");
      }
      return [];
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  /**
   * Initiate OAuth flow for a platform using popup window
   */
  const startOAuthForPlatform = async (platformId, mode = "connect") => {
    try {
      if (mode === "reconnect") {
        setReconnectingPlatform(platformId);
      } else {
        setConnectingPlatform(platformId);
      }
      
      // Get OAuth URL with returnUrl pointing back to Infinium
      const returnUrl = `${window.location.origin}/performance-marketing`;
      const result = await postizAPI.getOAuthUrl(token, platformId, returnUrl);
      
      if (result.success && result.data?.oauthUrl) {
        // Open OAuth in popup window (width: 500px, height: 700px)
        const width = 500;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popupWindow = window.open(
          result.data.oauthUrl,
          `oauth-${platformId}`,
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popupWindow) {
          toast.error('Popup blocked! Please allow popups for this site.');
          if (mode === "reconnect") {
            setReconnectingPlatform(null);
          } else {
            setConnectingPlatform(null);
          }
          return;
        }

        // Poll while popup is open so UI updates as soon as OAuth completes.
        let attempts = 0;
        const maxAttempts = 90;
        const popupCheckInterval = setInterval(() => {
          attempts += 1;

          const runCheck = async () => {
            const latest = await fetchConnectors({ showLoading: false, showErrorToast: false });
            const connectedNow = latest.some(
              (connector) => 
                normalizePlatform(connector.platform) === normalizePlatform(platformId) &&
                !connector.inBetweenSteps &&
                !connector.refreshNeeded &&
                !connector.disabled
            );

            if (connectedNow) {
              clearInterval(popupCheckInterval);
              try {
                popupWindow.close();
              } catch {
                // Ignore cross-window close failures.
              }
              toast.success(`${platformId} connected successfully!`);
              if (mode === "reconnect") {
                setReconnectingPlatform(null);
              } else {
                setConnectingPlatform(null);
              }
              return;
            }

            if (popupWindow.closed || attempts >= maxAttempts) {
              clearInterval(popupCheckInterval);
              if (mode === "reconnect") {
                setReconnectingPlatform(null);
              } else {
                setConnectingPlatform(null);
              }
              await fetchConnectors({ showLoading: false, showErrorToast: false });
            }
          };

          runCheck().catch((error) => {
            clearInterval(popupCheckInterval);
            console.error("Error polling OAuth connector state:", error);
            if (mode === "reconnect") {
              setReconnectingPlatform(null);
            } else {
              setConnectingPlatform(null);
            }
          });
        }, 1000);
      } else {
        toast.error(`Could not connect to ${platformId}`);
        if (mode === "reconnect") {
          setReconnectingPlatform(null);
        } else {
          setConnectingPlatform(null);
        }
      }
    } catch (err) {
      console.error("Error connecting platform:", err);
      toast.error(`Failed to connect ${platformId}`);
      if (mode === "reconnect") {
        setReconnectingPlatform(null);
      } else {
        setConnectingPlatform(null);
      }
    }
  };

  const handleConnectPlatform = async (platformId) => {
    await startOAuthForPlatform(platformId, "connect");
  };

  const handleReconnectPlatform = async (platformId) => {
    await startOAuthForPlatform(platformId, "reconnect");
  };

  /**
   * Disconnect a social platform with confirmation
   */
  const handleDisconnectPlatform = async (integrationId, platformName) => {
    try {
      setDisconnectingId(integrationId);
      const result = await postizAPI.disconnectIntegration(token, integrationId);
      
      if (result.success) {
        toast.success(`${platformName} disconnected successfully`);
        // Refresh connectors list
        await fetchConnectors();
      } else {
        toast.error(`Failed to disconnect ${platformName}`);
      }
    } catch (err) {
      console.error("Error disconnecting platform:", err);
      toast.error(`Error disconnecting ${platformName}`);
    } finally {
      setDisconnectingId(null);
      setShowConfirmDisconnect(null);
    }
  };

  /**
   * Get platform object from ID
   */
  const getPlatform = (platformId) => {
    const normalizedId = normalizePlatform(platformId);
    return SOCIAL_PLATFORMS.find((p) => normalizePlatform(p.id) === normalizedId);
  };

  /**
   * Check if a platform is connected
   */
  const isConnected = (platformId) => {
    const normalizedId = normalizePlatform(platformId);
    return connectors.some((c) => normalizePlatform(c.platform) === normalizedId);
  };

  const getConnectorStatus = (connector) => {
    if (connector.disabled) {
      return {
        label: "Disabled",
        message: "This channel is disabled in Postiz. Reconnect to re-enable posting.",
        className: "bg-gray-600 text-white",
      };
    }

    if (connector.refreshNeeded || connector.inBetweenSteps) {
      return {
        label: "Needs Reconnect",
        message: "Token refresh is required before this channel can publish.",
        className: "bg-amber-600 text-white",
      };
    }

    return {
      label: "Connected",
      message: "Ready for scheduling and publishing.",
      className: "bg-green-600 text-white",
    };
  };

  const visibleConnectors = connectors.filter((connector) =>
    VISIBLE_PLATFORM_IDS.has(normalizePlatform(connector.platform))
  );

  if (loading) {
    return (
      <div className={`flex justify-center items-center py-12 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="inline-block">
            <div className={`w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin`}></div>
          </div>
          <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading social platforms...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 rounded-lg border-2 border-red-500 ${isDarkMode ? 'bg-gray-800' : 'bg-red-50'}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">⚠️</span>
          <div>
            <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-red-900'}`}>
              Error Loading Platforms
            </p>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-red-700'}`}>
              {error}
            </p>
          </div>
        </div>
        <button
          onClick={fetchConnectors}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-lg p-6`}>
      <div className="mb-8">
        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Social Platforms
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Connect your social media accounts to schedule and publish content across multiple platforms.
        </p>
      </div>

      {/* Connected Platforms Section */}
      {visibleConnectors.length > 0 && (
        <div className="mb-8">
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            ✓ Connected Platforms ({visibleConnectors.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleConnectors.map((connector) => {
              const platform = getPlatform(connector.platform);
              const status = getConnectorStatus(connector);
              const accountLabel = connector.display || connector.name || connector.customer || null;
              return (
                <div
                  key={connector.id}
                  className={`p-4 rounded-lg border-2 border-green-500 ${
                    isDarkMode ? 'bg-gray-800' : 'bg-green-50'
                  } relative`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{platform?.icon || "🔗"}</span>
                      <div>
                        <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {platform?.name || connector.platform}
                        </p>
                        {accountLabel && (
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {accountLabel}
                          </p>
                        )}
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {status.message}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${status.className}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Disconnect Button */}
                  <div className="mt-3 flex gap-2">
                    {showConfirmDisconnect === connector.id ? (
                      <div className={`text-xs p-2 rounded border ${isDarkMode ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-300'}`}>
                        <p className={isDarkMode ? 'text-red-200' : 'text-red-700'}>
                          Are you sure? This connection will be removed.
                        </p>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() =>
                              handleDisconnectPlatform(connector.id, platform?.name || connector.platform)
                            }
                            disabled={disconnectingId === connector.id}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition disabled:opacity-50"
                          >
                            {disconnectingId === connector.id ? "Disconnecting..." : "Yes, Disconnect"}
                          </button>
                          <button
                            onClick={() => setShowConfirmDisconnect(null)}
                            className={`px-3 py-1 rounded text-xs transition ${
                              isDarkMode
                                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                                : 'bg-gray-300 hover:bg-gray-400 text-gray-800'
                            }`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {(connector.refreshNeeded || connector.inBetweenSteps || connector.disabled) && (
                          <button
                            onClick={() => handleReconnectPlatform(connector.platform)}
                            disabled={reconnectingPlatform === connector.platform}
                            className={`px-3 py-1 text-xs font-medium rounded transition disabled:opacity-50 ${
                              isDarkMode
                                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                                : 'bg-amber-500 hover:bg-amber-600 text-white'
                            }`}
                          >
                            {reconnectingPlatform === connector.platform ? "Reconnecting..." : "Reconnect"}
                          </button>
                        )}
                        <button
                          onClick={() => setShowConfirmDisconnect(connector.id)}
                          className={`flex-1 px-3 py-1 text-xs font-medium rounded transition ${
                            isDarkMode
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-red-500 hover:bg-red-600 text-white'
                          }`}
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Platforms Section */}
      <div>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          Available Platforms ({VISIBLE_PLATFORMS.filter(platform => !isConnected(platform.id)).length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {VISIBLE_PLATFORMS.filter(platform => !isConnected(platform.id)).map((platform) => (
            <button
              key={platform.id}
              onClick={() => handleConnectPlatform(platform.id)}
              disabled={connectingPlatform === platform.id}
              className={`p-4 rounded-lg border-2 transition hover:shadow-sm ${
                isDarkMode
                  ? 'bg-gray-800 border-gray-700 hover:border-blue-500'
                  : 'bg-white border-gray-200 hover:border-blue-500'
              } disabled:opacity-50`}
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl">{platform.icon}</span>
                <div className="text-left flex-1">
                  <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {platform.name}
                  </p>
                </div>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full transition ${
                  connectingPlatform === platform.id
                    ? 'bg-gray-500 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}>
                  {connectingPlatform === platform.id ? "Connecting..." : "Connect"}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className={`mt-8 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-blue-50'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-blue-900'}`}>
          💡 <span className="font-semibold">Tip:</span> Connect your social accounts to schedule and publish content.
          If a channel is disabled or needs refresh, use Reconnect to restore posting access.
        </p>
      </div>
    </div>
  );
}
